/**
 * The one-number Inbox button (friendly Mailroom slice 1, mockup
 * https://claude.ai/artifact/6yqk6vzqGKEcCpJMKEgiU8 Part 1). Behind the
 * per-person "Try the new Mailroom" switch (user preference mailroom_v2_view).
 *
 *   - The number is EMAILS ONLY (Corbin, 10/9): unassigned + assigned to you,
 *     from GET /mailbox/inbox-peek — the same rows and the same rule as
 *     GET /mailbox/count. Tasks keep their own counter beside it.
 *   - Gray at 0 ("all caught up"), orange at 1 or more. Off the Mailroom team
 *     (Corbin) it is always gray.
 *   - Hover (or tap on a phone) shows the top five with ONE button each, plus
 *     a quiet Done. Done turns the row into "Done · Undo" in place.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Mail, Undo2, X } from "lucide-react";
import { api } from "@/lib/api";
import { mailThreadHref, type MailPeek, type MailPeekItem } from "@/lib/inbox";
import { MAIL_TYPE_LOOK } from "@/pages/mailroom/mail-type";
import { nextStep } from "@/pages/mailroom/next-step";
import { TeachIntro } from "@/teach/teach-card";
import { useTeachUsed } from "@/teach/teach-state";

/** What the chip says, from the peek. Pure so the gray/orange rule is tested. */
export function chipState(peek: MailPeek | undefined): { n: number; hot: boolean; label: string } {
    const n = peek?.onTeam === false ? 0 : (peek?.count ?? 0);
    return {
        n,
        hot: n > 0,
        label:
            n > 0 ? `Inbox — ${n} email${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} you` : "Inbox — all caught up",
    };
}

interface DoneRow {
    item: MailPeekItem;
    /** Who owned it before Done, so Undo can hand it back. */
    priorAssigneeId: string | null;
}

export function InboxChip({ onNavigate }: { onNavigate?: () => void }) {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const peekQ = useQuery({
        queryKey: ["mailbox", "inbox-peek"],
        queryFn: () => api.get<MailPeek>("/api/mailbox/inbox-peek"),
        refetchInterval: 60_000,
    });
    const peek = peekQ.data;
    const { n, hot, label } = chipState(peek);
    const [open, setOpen] = useState(false);
    const [done, setDone] = useState<Record<string, DoneRow>>({});
    // Teach cards (#1838): opening the preview is using it.
    useTeachUsed("mailroom.chip", open);
    const onMailroom = useLocation().pathname.startsWith("/mailroom");
    const wrapRef = useRef<HTMLDivElement>(null);
    const popRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    // The preview renders in a portal on <body>: the toolbar's scoped CSS
    // (.tbv3 .grid …) would otherwise restyle its rows. Placed under the chip.
    const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Close on Escape and on a click outside.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (wrapRef.current?.contains(t) || popRef.current?.contains(t)) return;
            setOpen(false);
        };
        const place = () => {
            const r = btnRef.current?.getBoundingClientRect();
            if (r) setAnchor({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
        };
        place();
        window.addEventListener("resize", place);
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
            window.removeEventListener("resize", place);
        };
    }, [open]);
    // Forget Done rows once the preview closes — they are gone from the count.
    useEffect(() => {
        if (!open) setDone({});
    }, [open]);

    const refresh = () => {
        void qc.invalidateQueries({ queryKey: ["mailbox"] });
    };
    const markDone = useMutation({
        mutationFn: (t: MailPeekItem) => api.post(`/api/mailbox/threads/${t.id}/work`, { action: "marked_done" }),
        // worked_by also records who last acted; only a "working" email has an owner to hand back.
        onMutate: (t) =>
            setDone((d) => ({
                ...d,
                [t.id]: { item: t, priorAssigneeId: t.workState === "working" ? (t.assigneeId ?? null) : null },
            })),
        onError: (_e, t) =>
            setDone((d) => {
                const next = { ...d };
                delete next[t.id];
                return next;
            }),
        onSettled: refresh,
    });
    const undo = useMutation({
        mutationFn: async (row: DoneRow) => {
            await api.post(`/api/mailbox/threads/${row.item.id}/work`, { action: "reopen" });
            if (row.priorAssigneeId) {
                await api.post(`/api/mailbox/threads/${row.item.id}/assign`, { userId: row.priorAssigneeId });
            }
        },
        onSuccess: (_r, row) =>
            setDone((d) => {
                const next = { ...d };
                delete next[row.item.id];
                return next;
            }),
        onSettled: refresh,
    });

    // Keep a Done row on screen (as "Done · Undo") even after the refetch drops it.
    const live = peek?.threads ?? [];
    const rows: MailPeekItem[] = [...live];
    for (const d of Object.values(done)) if (!rows.some((r) => r.id === d.item.id)) rows.push(d.item);

    const go = (href: string) => {
        setOpen(false);
        onNavigate?.();
        navigate(href);
    };
    const enter = () => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        // Hover opens only where hover is real (a mouse), never on a phone tap.
        if (typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches) {
            hoverTimer.current = setTimeout(() => setOpen(true), 150);
        }
    };
    const leave = () => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        if (typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches) {
            hoverTimer.current = setTimeout(() => setOpen(false), 300);
        }
    };

    const stillOpen = n - Object.keys(done).filter((id) => live.some((r) => r.id === id)).length;
    return (
        <div ref={wrapRef} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
            {/* Off the Mailroom, so its own three cards come first there. */}
            <TeachIntro featureKey="mailroom.chip" when={!onMailroom} />
            <button
                ref={btnRef}
                type="button"
                data-testid="inbox-chip"
                data-hot={hot ? "true" : "false"}
                className={
                    hot
                        ? "door !border-orange-600 !bg-orange-600 !text-white hover:!bg-orange-700 dark:!border-orange-500 dark:!bg-orange-500 dark:!text-stone-950"
                        : "door !text-muted-foreground"
                }
                aria-label={label}
                aria-expanded={open}
                aria-haspopup="dialog"
                onClick={() => setOpen((v) => !v)}
            >
                <Mail size={15} />
                {hot ? "Inbox" : <span className="hidden sm:inline">Inbox · all caught up</span>}
                {!hot && <span className="sm:hidden">Inbox</span>}
                {hot && (
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-2 text-sm font-extrabold tabular-nums text-orange-700 dark:bg-stone-950 dark:text-orange-300">
                        {n}
                    </span>
                )}
            </button>

            {open &&
                createPortal(
                    <div
                        ref={popRef}
                        role="dialog"
                        aria-label="What needs you in the Mailroom"
                        data-testid="inbox-preview"
                        onMouseEnter={enter}
                        onMouseLeave={leave}
                        style={
                            anchor && typeof window !== "undefined" && window.innerWidth >= 640
                                ? { top: anchor.top, right: anchor.right }
                                : undefined
                        }
                        className="fixed inset-x-2 top-16 z-[60] overflow-hidden rounded-xl border border-border bg-card text-foreground shadow-2xl sm:inset-x-auto sm:w-[420px]"
                    >
                        <div className="flex items-baseline justify-between gap-2 px-4 pb-2 pt-3">
                            <b className="text-base font-extrabold">
                                {peek?.onTeam === false
                                    ? "The team's Mailroom"
                                    : stillOpen > 0
                                      ? `${stillOpen} need${stillOpen === 1 ? "s" : ""} you`
                                      : "Nothing needs you"}
                            </b>
                            <span className="flex items-center gap-2 text-xs text-muted-foreground">
                                {peek?.oldestWaitingLabel && stillOpen > 0 && <>oldest {peek.oldestWaitingLabel}</>}
                                <button
                                    type="button"
                                    aria-label="Close"
                                    onClick={() => setOpen(false)}
                                    className="rounded p-1 hover:bg-muted sm:hidden"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </span>
                        </div>
                        {rows.length === 0 ? (
                            <div className="flex flex-col items-center gap-1 border-t border-border px-4 py-6 text-center text-sm text-muted-foreground">
                                <Check className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                                {peek?.onTeam === false
                                    ? "You're not on the Mailroom team, so nothing here is yours."
                                    : "You're caught up."}
                            </div>
                        ) : (
                            <ul className="max-h-[60vh] overflow-y-auto">
                                {rows.map((t) => {
                                    const d = done[t.id];
                                    if (d) {
                                        return (
                                            <li
                                                key={t.id}
                                                className="grid grid-cols-[34px_1fr_auto] items-center gap-2.5 border-t border-border px-4 py-2"
                                            >
                                                <span className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                                                    <Check className="h-4 w-4" />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block text-sm font-semibold">Done</span>
                                                    <span className="block truncate text-xs text-muted-foreground">
                                                        {t.customerName || t.from}
                                                    </span>
                                                </span>
                                                <button
                                                    type="button"
                                                    disabled={undo.isPending}
                                                    onClick={() => undo.mutate(d)}
                                                    className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold text-muted-foreground hover:bg-muted hover:text-foreground"
                                                >
                                                    <Undo2 className="h-3.5 w-3.5" /> Undo
                                                </button>
                                            </li>
                                        );
                                    }
                                    const look = MAIL_TYPE_LOOK[t.mailType ?? "unsorted"];
                                    const { Icon } = look;
                                    const step = nextStep({
                                        mailType: t.mailType,
                                        workState: t.workState ?? "needs_work",
                                        weRepliedLast: t.weRepliedLast,
                                        attachmentCount: t.attachmentCount,
                                    });
                                    const late = (t.waitedHours ?? 0) >= 4;
                                    return (
                                        <li
                                            key={t.id}
                                            className="grid grid-cols-[34px_1fr_auto] items-center gap-2.5 border-t border-border px-4 py-2"
                                        >
                                            <span
                                                className={`grid h-[34px] w-[34px] place-items-center rounded-lg ${look.tile}`}
                                                title={look.label}
                                            >
                                                <Icon className="h-4 w-4" />
                                            </span>
                                            <button
                                                type="button"
                                                className="min-w-0 text-left"
                                                onClick={() => go(mailThreadHref(t.id, t.accountId))}
                                            >
                                                <span className="block truncate text-sm font-semibold">
                                                    {t.customerName || t.from}
                                                </span>
                                                <span className="block truncate text-xs text-muted-foreground">
                                                    {t.subject || "(no subject)"}
                                                    {t.waitedLabel && (
                                                        <span
                                                            className={
                                                                late
                                                                    ? "font-bold text-orange-700 dark:text-orange-300"
                                                                    : ""
                                                            }
                                                        >
                                                            {" "}
                                                            · {t.waitedLabel}
                                                        </span>
                                                    )}
                                                </span>
                                            </button>
                                            <span className="flex items-center gap-1">
                                                {step.kind !== "done" && (
                                                    <button
                                                        type="button"
                                                        title="Done"
                                                        aria-label={`Done: ${t.subject ?? t.from}`}
                                                        onClick={() => markDone.mutate(t)}
                                                        className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                                                    >
                                                        <Check className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        step.kind === "done"
                                                            ? markDone.mutate(t)
                                                            : go(mailThreadHref(t.id, t.accountId, step.kind))
                                                    }
                                                    className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold ${
                                                        late
                                                            ? "bg-orange-600 text-white hover:bg-orange-700 dark:bg-orange-500 dark:text-stone-950"
                                                            : "border border-border hover:bg-muted"
                                                    }`}
                                                >
                                                    {step.label}
                                                </button>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        <div className="flex flex-wrap justify-between gap-2 bg-muted/50 px-4 py-2.5 text-xs text-muted-foreground">
                            <span>
                                {peek?.team
                                    ? `Team: ${peek.team.open} open · ${peek.team.waiting} waiting on customer`
                                    : ""}
                            </span>
                            <button
                                type="button"
                                onClick={() => go("/mailroom")}
                                className="font-bold text-[#7A5A0A] hover:underline dark:text-amber-300"
                            >
                                Open Mailroom →
                            </button>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
